<template>
  <wizzard-container :infos="infos">
    <div class="title">{{ $t('Form Gallery Title') }}</div>
    <p class="caption">{{ $t('Form Gallery Subtitle business') }}</p>

    <media-library-collection
      v-if="galleryLoaded"
      name="collection"
      :initial-value="gallery"
      :validation-rules="{
        accept: ['image/png', 'image/jpeg'],
        maxSizeInKB: 1024 * 5,
        minSizeInKB: 50,
      }"
      :max-items="20"
      :upload-domain="uploadDomain"
      :translations="translations"
      @change="onChangeCollection"
      @is-ready-to-submit-change="isReadyToSubmit = !$event"
    />

    <div v-if="!gallery.length && googlePhotos.length" class="mt-12">
      <div class="subtitle-1 mb-3">Google {{ $t('Photos') }}</div>
      <v-row>
        <v-col
          v-for="(photo, index) in googlePhotosInline"
          :key="`google_photo_${index}`"
          cols="6"
          sm="6"
        >
          <a
            v-if="photo.googleMapsUri"
            :href="photo.googleMapsUri"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-card outlined>
              <v-img
                :src="photo.url"
                aspect-ratio="1"
                class="grey lighten-2"
              />
              <v-card-text class="py-2 text-caption">
                <div v-if="photo.authorAttributionsDisplayName">
                  <v-icon small>mdi-copyright</v-icon>
                  <a :href="photo.authorAttributionsUri" target="_blank" rel="noopener noreferrer">
                      {{ photo.authorAttributionsDisplayName }}
                  </a>
                </div>
              </v-card-text>
            </v-card>
          </a>
        </v-col>
      </v-row>
      <v-btn
        v-if="hasMoreGooglePhotos"
        class="mt-6 text-capitalize"
        color="primary"
        outlined
        @click="showGooglePhotosModal = true"
      >
        {{ $t('Show more') }} (+{{ googlePhotos.length - inlineGooglePhotosLimit }})
      </v-btn>
    </div>

    <modal
      v-if="showGooglePhotosModal"
      :show="true"
      :title="`Google ${ $t('Photos') }`"
      max-width="980"
      @close="showGooglePhotosModal = false"
      @save="showGooglePhotosModal = false"
      hide-actions-cancel
      save-text="Close"
    >
      <v-card-text>
        <v-row>
          <v-col
            v-for="(photo, index) in googlePhotos"
            :key="`google_photo_modal_${index}`"
            cols="12"
            sm="6"
            md="4"
          >
            <a
              v-if="photo.googleMapsUri"
              :href="photo.googleMapsUri"
              target="_blank"
            >
              <v-card outlined>
                <v-img
                  :src="photo.url"
                  aspect-ratio="1"
                  class="grey lighten-2"
                />
                <v-card-text class="py-2 text-caption">
                  <div v-if="photo.authorAttributionsDisplayName">
                    <v-icon small>mdi-copyright</v-icon>
                    <a :href="photo.authorAttributionsUri" target="_blank" rel="noopener noreferrer">
                        {{ photo.authorAttributionsDisplayName }}
                    </a>
                  </div>
                </v-card-text>
              </v-card>
            </a>
          </v-col>
        </v-row>
      </v-card-text>
    </modal>

    <form-action-buttons
      status="listed"
      :loading="loadingBtn"
      @submit="submit"
    />
  </wizzard-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import MediaLibraryMixin from '@/lib/MediaLibraryMixin';
import Modal from '@/components/Modal.vue';
import FormActionButtons from '@/views/ads/forms/FormActionButtons.vue';
import WizzardContainer from '@/views/ads/forms/WizzardContainer.vue';

export default {
  mixins: [MediaLibraryMixin],
  components: {
    Modal,
    FormActionButtons,
    WizzardContainer,
  },
  props: {
    businessId: { type: String, required: true },
    business: { type: Object, default: null },
  },
  data() {
    return {
      gallery: [],
      isReadyToSubmit: false,
      galleryLoaded: false,
      inlineGooglePhotosLimit: 5,
      showGooglePhotosModal: false,
    };
  },
  computed: {
    ...mapGetters(['loading']),
    googlePhotos() {
      if (!this.business) {
        return [];
      }
      const formattedPhotos = this.business.formatted_photos || this.business.formattedPhotos;
      if (!Array.isArray(formattedPhotos)) {
        return [];
      }
      return formattedPhotos;
    },
    googlePhotosInline() {
      return this.googlePhotos.slice(0, this.inlineGooglePhotosLimit);
    },
    hasMoreGooglePhotos() {
      return this.googlePhotos.length > this.inlineGooglePhotosLimit;
    },
    loadingBtn() {
      return this.isReadyToSubmit || this.loading;
    },
    infos() {
      return [
        {
          icon: 'green-energy',
          title: 'Business Form Gallery InfoTitle',
          description: 'Business Form Gallery InfoDescription',
        },
      ];
    },
  },
  watch: {
    business: {
      immediate: true,
      handler(value) {
        if (!value) {
          return;
        }
        if (Array.isArray(value.gallery)) {
          this.gallery = value.gallery;
        } else if (value.gallery && typeof value.gallery === 'object') {
          this.gallery = Object.values(value.gallery);
        } else {
          this.gallery = [];
        }
        this.galleryLoaded = true;
      },
    },
  },
  methods: {
    onChangeCollection(media) {
      this.gallery = media;
    },
    submit() {
      this.$store.dispatch('loading', true);
      axios
        .post(`panel/ads/validation/business/gallery/${this.businessId}`, {
          media: this.gallery,
        })
        .then(() => {
          this.$store.dispatch('clearErrors');
          this.$store.dispatch('message', this.$t('Saved successfully'));
          this.$emit('success');
        })
        .catch((error) => {
          const errors = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors
            : {};
          this.$store.dispatch('setErrors', errors);
        })
        .finally(() => {
          this.$store.dispatch('loading', false);
        });
    },
  },
};
</script>
